(ns frontend.view.modals.curtains.nev-editor-curtain.view
  (:require
   [common.attribute :as attribute]
   [common.entity :as entity]
   [common.intelligence.common :as ic]
   [common.localization :as loc]
   [common.schema.constraints :as constraints]
   [common.schema.data-types :as data-types]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.actions.entity :as entity-actions]
   [frontend.components.field-editor :as field-editor]
   [frontend.intelligence.icon :as intelligence-icon]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.options-management.view :as options-management]
   [potpuri.core :refer [fn-> fn->>]]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.util :as uu]))

(defonce modal-key :curtain/nev-editor-curtain)

(defn entity-disabled?
  [entity-id]
  (let [entity @(r/track session/get-entity entity-id)]
    (or (nil? entity)
        (:common/disabled? entity))))

(defn processing
  [data-state]
  [:div
   {:class (uu/cm "p-8 relative hidden left-0 right-0 bottom-0 top-0" (when (:processing? @data-state) "flex justify-center items-center"))}
   [:div
    {:class "flex flex-col gap-3"}
    [:i.fal.fa-spinner.fa-pulse.fa-4x]
    [:span (loc/tr :processing)]]])

;; some fields are :editor.type/none (i.e. shouldn't be visible in the original
;;  entity-editor) e.g. tag-fields, that should still be editable here:
(defn update-editor
  [{:field/keys [default-visibility data-type] :as field-schema}]
  (let [editor-type (or (:editor/type field-schema)
                        (:editor-type field-schema)
                        (-> default-visibility
                            :editor))
        default-data-type-editor (->> data-types/zapflow-data-types
                                      data-type
                                      :default-editor)]
    (if (= editor-type :editor.type/none)
      (assoc field-schema
        :editor/type
        default-data-type-editor)
      field-schema)))

(defn input
  [{:keys [_field-schema attribute entity edit-state]}]
  (let [base-entity (fu/base-entity entity attribute)
        base-entity-id (:db/id base-entity)
        kw-attribute (if (keyword? attribute)
                       attribute
                       (last attribute))
        entity-edit-state (r/cursor edit-state [base-entity-id])
        edit! (fn [attribute new-value]
                ;;field-editor passes a proper kw-attribute (might be secondary-attribute):
                (swap! entity-edit-state assoc attribute new-value))]
    (fn [{:keys [field-schema attribute]}]
      (let [tenant-schema @session/tenant-schema
            current-value (get base-entity kw-attribute)
            current-value? (contains? base-entity kw-attribute)
            field-schema (update-editor field-schema)
            base-entity (fu/base-entity entity attribute)
            entity-type (:entity/type base-entity)
            enriched-entity (when entity-type
                              (ic/enrich-entity base-entity))
            intelligence-value (get enriched-entity kw-attribute)
            intelligence-value? (contains? enriched-entity kw-attribute)
            pipeline-attribute (pipeline/pipeline-key entity-type)
            edits @entity-edit-state
            edit-value? (contains? edits kw-attribute)
            value (cond
                    edit-value?
                    (get edits kw-attribute)

                    current-value?
                    current-value

                    intelligence-value?
                    intelligence-value

                    :else
                    nil)
            is-intelligence-value? (and intelligence-value?
                                        (not current-value?)
                                        (not edit-value?))]
        [field-editor/view
         (merge
          field-schema
          {:value             value
           :attribute         kw-attribute
           :containing-entity base-entity
           :field/attribute   (if (vector? attribute)
                                (last attribute)
                                attribute)
           :tools             (concat
                               [:tool-item/clear]
                               (when (and (session/is-admin?)
                                          (->> field-schema
                                               :named-value-attribute))
                                 [:tool-item/settings]))
           :entity-types-atom session/entity-types
           :entity-type       entity-type
           :drag-options      {:hide-drag-area? true}
           :open-settings!    (fn []
                                (modals/open-modal! options-management/modal-key
                                                    {:pipeline-attribute pipeline-attribute
                                                     :selected-field     kw-attribute}))
           :validate!         (fn [attribute value]
                                (let [field-schema (registry/get-field-schema tenant-schema
                                                                              attribute)
                                      field-constraints (:field/constraints field-schema)
                                      errors (constraints/validate field-constraints
                                                                   (:db/id entity)
                                                                   value
                                                                   entity
                                                                   (partial session/unique-check
                                                                            false
                                                                            (partial
                                                                             routing/open-entity))
                                                                   loc/tr
                                                                   logging/warn)]
                                  (when-not errors
                                    (edit! attribute value))
                                  errors))
           :on-save!          (fn [attribute new-value on-success! _on-error!]
                                (edit! attribute new-value)
                                ;; slight delay needed for select-components:
                                (r/after-render
                                 on-success!))}
          (when is-intelligence-value?
            {:source-options {:source-icon-fn intelligence-icon/view}})
          (when-let [secondary-attribute (:field/secondary-attribute field-schema)]
            {:secondary-view-args
             (let [sec-fs (registry/get-field-schema tenant-schema secondary-attribute)
                   current-sec-value (get base-entity secondary-attribute)
                   default-currency (:tenant/default-currency @session/tenant)
                   val (or current-sec-value default-currency)]
               (merge
                sec-fs
                {:value (if (contains? edits secondary-attribute)
                          (get edits secondary-attribute)
                          val)}))}))]))))

(defn edit-fields
  [{:keys [edit-state entity fields schema-lookup]}]
  (into
   [:div
    {:class "px-2"}]
   (for [field fields]
     [:div
      [input
       {:attribute    field
        :entity       entity
        :edit-state   edit-state
        :field-schema (get schema-lookup field)}]])))

(defn force-close-view
  [{:keys [close-modal!]}]
  [:div
   [:div
    {:class "mt-5 text-center"}
    [:img
     {:src "/img/Company-is-deleted.svg"}]
    [:p
     {:class "py-5"}
     (loc/tr :no-access-or-deleted)]]
   [:div
    {:class "text-center"}
    [button/primary-solid
     {:on-click close-modal!}
     [:span (loc/tr :close)]]]])

(defn get-fields
  [field-defs]
  (->> field-defs
       (map :fields)
       (mapcat vals)
       (keep :field)))

(def wide-data-types
  #{:data.type/text-simple
    :data.type/text-multiline
    :data.type/text-richtext})

(def wide-editor-types
  #{:editor.type/grid
    :editor.type/financials
    :editor.type/tesi-financials
    :editor.type/tesi-financials-2})

(defn wide-field?
  [{:keys [field/data-type field/default-visibility]}]
  (let [editor-type (:editor default-visibility)]
    (or (contains? wide-data-types data-type)
        (contains? wide-editor-types editor-type))))

(defn group-field
  [schema-lookup field]
  (let [field-schema (get schema-lookup field)]
    (cond (wide-field? field-schema)
          :wide

          (nil? field-schema)
          :not-in-use

          :else
          :narrow)))

(defn new-vals
  [entity-id edits]
  (let [current-vals (session/get-entity entity-id)]
    (->> edits
         (reduce-kv (fn [m k v]
                      (if (not= v (get current-vals k))
                        (assoc m k v)
                        m))
                    {}))))

(defn save!
  [{:keys [state edit-state close-modal!]}]
  (let [all-edits @edit-state
        ;;only edits for entities that are found in session-data:
        non-disabled-edits (->> all-edits
                                (cu/remove-kv (fn-> first entity-disabled?)))
        ;; filter edits that differ from current values:
        modified-edits (->> non-disabled-edits
                            (reduce-kv (fn [m entity-id entity-edits]
                                         (let [new-edits (new-vals entity-id entity-edits)]
                                           (if (empty? new-edits)
                                             m
                                             (assoc m entity-id new-edits))))
                                       {}))]
    ;;TODO also skip edits where no access (e.g. if oppo's pipeline changes and
    ;;     new pipeline has no access to a field that is already edited
    (if (empty? modified-edits)
      (close-modal!)
      (doseq [[entity-id edits] modified-edits]
        (swap! state assoc-in [:processing-entities entity-id])
        (entity-actions/*->entity-update!
         entity-id
         edits
         (fn []
           (swap! edit-state dissoc entity-id)
           (swap! state update :processing-entities dissoc entity-id)
           (when (and (empty? (:processing-entitites @state))
                      (empty? (:errors @state)))
             (close-modal!)))
         (fn [err]
           (let [error-msg (-> err
                               ex-data
                               :message)]
             (logging/error "Failed to update " entity-id error-msg)
             (swap! state assoc-in [:errors entity-id] error-msg)
             (swap! state update :processing-entities dissoc entity-id))))))))

;;TODO also track :oppo/pipeline (& equivalent) change?

;; example: base-entity oppo, ref-attribute :oppo/target-asset
;;  comments inside fns refer to this example case:

;; track if oppo's :oppo/target-asset is updated (by someone else) during edit:
(defn track-ref-val
  [base-entity-atom ref-attribute initial-value on-updated!]
  (let [entity @base-entity-atom
        value (get entity ref-attribute)]
    (when (not= value initial-value)
      ;; oppo's target-asset has been updated => the asset's edit-values
      ;; for target-asset fields point to a different target-asset
      (on-updated!))))

;; reference entities and values to track:
(defn get-ref-tracks
  [base-entity-id fields]
  ;; [[sales-oppo-id :sales-opportunity current-customer-id
  ;;  [customer-id :investor/legal-entity current-legal-entity-id]]
  ;;if customer is a company/person/nil - only [sales-oppo-id :sales-opportunity/customer current-customer-id].
  ;; "base" refers to first layer of indirection/reference:
  (let [ref-attributes (->> fields
                            (filter vector?))
        base-ref-attributes (->> ref-attributes
                                 (filter (fn->> count (= 2)))
                                 (map first)
                                 distinct)
        entity (session/get-entity base-entity-id)
        base-tracks (->> base-ref-attributes
                         (filter (fn->> (get entity)))
                         (map (fn [attribute]
                                [base-entity-id attribute (get entity attribute)])))
        ;; more than one layer of indirection/references:
        rest-tracks (->> ref-attributes
                         (filter (fn->> count (< 2)))
                         (group-by first)
                         (filter (fn [[base-entity-attribute _]]
                                   ;;e.g. :sales-opportunity/customer
                                   (let [val (get entity base-entity-attribute)]
                                     (and (number? val)
                                          (session/get-entity val)))))
                         (mapcat (fn [[base-entity-attribute ref-attributes]]
                                   (let [ref-entity-id (get entity base-entity-attribute)]
                                     (get-ref-tracks ref-entity-id
                                                     (->> ref-attributes
                                                          (map (fn [ref-attribute]
                                                                 ;;move on layer deeper:
                                                                 (subvec ref-attribute 1)))))))))
        tracks (concat base-tracks
                       ;; rest - when ref-attribute contains more than 2 fields:
                       rest-tracks)]
    tracks))
;;example:
;;(get-ref-tracks test-e test-f)
;; => ([17592186519633 :sales-opportunity/customer 17592186313408]
;;     [17592186313408 :investor/legal-entity 17592186295980])
;; [entity-id attribute val] needs to be tracked:
;;  if entity-id's value changes, the val-entity's edits now refer to old entity.
;; also - all entity-ids (i.e. their entities) must be tracked for deletion (->> x (map first) distinct)

(defn initialize!
  [{:keys [props state]}]
  (let [{:keys [entity-id field-defs]} props
        fields (get-fields field-defs)
        ref-tracks (get-ref-tracks entity-id fields)]
    (swap! state assoc :ref-tracks ref-tracks)
    {:ref-tracks ref-tracks}))

;; whole view is obsolete if:
;;  -nev-entity has been disabled
;;  -ref-entity (e.g. :oppo/target-asset) has been disabled AND there are editable ref-entity fields in header
;;  -TODO no longer access rights - check pipeline access & feature access & module access & what else?
(defn obsolete?
  [{:keys [entity-id]}]
  (entity-disabled? entity-id))

(defn notifications
  [{:keys [ref-tracks edit-state]}]
  (let [tenant-schema @session/tenant-schema
        changes (->> ref-tracks
                     (map (fn [[entity-id attribute initial-value]]
                            [entity-id attribute initial-value
                             (get (session/get-entity entity-id) attribute)]))
                     (filter (fn [[_entity-id _attribute initial-value current-value]]
                               (not= initial-value current-value))))
        edit-changes (->> ref-tracks
                          (filter (fn [[entity-id attribute initial-value]]
                                    (let [edit-entity (get @edit-state entity-id)
                                          edit-value (get edit-entity attribute)]
                                      (and (contains? edit-entity attribute)
                                           (not= edit-value initial-value))))))]
    [:div
     {:class "bg-info-light"}
     (into
      (for [[_entity-id attribute _initial-value _current-value] changes
            :let [label (attribute/attribute-label tenant-schema attribute)]]
        [:div
         (str label
              " has been changed. Any changes to "
              label
              " fields affect old value.")]))
     (into
      (for [[_entity-id attribute initial-value _current-value] edit-changes
            :let [label (attribute/attribute-label tenant-schema attribute)
                  initial-entity (session/get-entity initial-value)
                  initial-entity-name (entity/display-string initial-entity)]]
        ;;TODO ensure reasonable functionality:
        [:div
         (str "You have changed "
              label
              " ")
         [button/primary-open
          (loc/tr :cancel)]

         (str ". Any changes to " label " fields will be applied to " initial-entity-name " ")
         [button/secondary-hollow
          {:on-click (fn [_]
                       ;;(swap! edit-local-state
                       (swap! edit-state dissoc initial-value)
                       )}
          (fu/format "Discard %s changes" initial-entity-name)]]))]))

(defn view
  [{:keys [_shown? _topmost? _close-modal! _props state] :as modal-props}]
  (let [edit-state (r/cursor state [:edit])
        init! (partial initialize! modal-props)]
    (init!)
    (fn [{:keys [shown? topmost? close-modal! props state]}]
      (let [{:keys [entity-id field-defs schema on-cancel!]} props
            entity (session/get-entity entity-id)
            fields (get-fields field-defs)
            schema-lookup (->> schema
                               (into {}))
            {:keys [wide narrow]} (group-by (partial group-field schema-lookup) fields)
            {:keys [processing-entities initial-values ref-tracks]} @state
            processing? (seq processing-entities)
            ref-entity-ids (->> ref-tracks
                                (map last))
            track-disabled-entity-ids (->> (concat [entity-id] ref-entity-ids)
                                           (remove nil?))
            force-close? (->> track-disabled-entity-ids
                              (map (fn [entity-id]
                                     {:entity-id entity-id}))
                              (some obsolete?))
            show-footer? (and (not processing?)
                              (not force-close?))
            header-text (loc/tr :edit-entity [(loc/tr (:entity/type entity))])
            button-disabled? false]
        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         close-modal!
          :backdrop      "static"
          :close-button? true}
         header-text
         [:<>
          (cond
            processing?
            [processing state]

            force-close?
            [force-close-view {:close-modal! close-modal!}]

            :else
            [:div
             ;;TODO show errors
             [notifications
              {:initial-values initial-values
               :ref-tracks     ref-tracks
               :state          state
               :edit-state     edit-state}]
             [:div
              {:class ""}
              [:div
               [edit-fields
                {:fields        wide
                 :entity        entity
                 :state         state
                 :edit-state    edit-state
                 :schema-lookup schema-lookup}]]
              [:div
               [edit-fields
                {:fields        narrow
                 :entity        entity
                 :state         state
                 :edit-state    edit-state
                 :schema-lookup schema-lookup}]]]])]
         (when show-footer?
           [:<>
            [modal/left-cancel-only
             (fn [_]
               (close-modal!)
               (when on-cancel!
                 (on-cancel!)))
             nil]
            [modal/right-single-button
             nil
             button-disabled?
             #(save! {:state        state
                      :edit-state   edit-state
                      :fields       fields
                      :close-modal! close-modal!})
             (loc/tr :save)
             nil]])]))))
